/**
 * grain (HOF-6) — the Preisspiegel chart's AGGREGATION granularity, and the
 * calendar arithmetic that implements it.
 *
 * It replaces `chartModel.Period` (the `1T/1W/1M/3M/6M/1J/Max` ZEITRAUM
 * control), and the two are not the same kind of thing at all, which is the
 * whole point of the swap: a Period chose HOW MUCH TIME the chart showed, over
 * an axis the reader then had to zoom and pan; a Grain chooses HOW MANY DEALS
 * ONE POINT STANDS FOR, over an axis that is fixed at one year. The first
 * answers "how far back can I look", which a fixed year now answers by itself;
 * the second answers "is this a day price or a month price", which nothing on
 * the old chart answered at all — it was one point per day, always, and a season
 * of daily deliveries drew as noise.
 *
 * Every bucket is expressed in EPOCH DAYS (UTC, no `Date` in the hot path, no
 * timezone), because the chart's x-axis is numeric epoch-ms and the aggregation
 * has to land on exactly the boundaries the axis draws.
 *
 * The week is the ISO week (Monday-start), cross-checked in `grain.test.ts`
 * against ernteprognose's `isoWeekStart`: both apps put a week-grain control in
 * front of the same farmer, and two definitions of "which Monday owns this
 * Thursday" would be visible to one reader switching tabs.
 */
import { toEpochDay } from './priceAt';

/** The aggregation grains the switcher offers, in reading order. */
export type Grain = 'day' | 'week' | 'month';

export const GRAINS: readonly Grain[] = ['day', 'week', 'month'];

/**
 * Stock-app style short labels, matching ernteprognose's Zeitraster control
 * (`1T | 1W | 1M | 1J`) rather than the issue's `1D`: this is a German UI and
 * the neighbouring chart already says `1T` for Tag. The German word rides along
 * as the `title` tooltip, so the accessible NAME stays exactly the visible text
 * (WCAG 2.5.3 "Label in Name") while the abbreviation is still explained.
 */
export const GRAIN_LABELS: Record<Grain, string> = { day: '1T', week: '1W', month: '1M' };
export const GRAIN_TITLES: Record<Grain, string> = {
  day: 'Tagespreis',
  week: 'Wochendurchschnitt',
  month: 'Monatsdurchschnitt',
};

/**
 * The grain an absent or unrecognised `?g=` resolves to (HOF-96 §1).
 *
 * Named rather than spelled twice, because TWO places have to agree on it and
 * they sit in different files: {@link resolveGrain} below, and the page's `?g=`
 * zero-value sentinel — the rule that keeps the default OUT of the URL. Let
 * those two drift and the default's own button goes inert (see the sentinel's
 * comment in `PreisspiegelPage.tsx`), which is a bug with no error, no console
 * line and nothing in the URL to see.
 */
export const DEFAULT_GRAIN: Grain = 'week';

/**
 * `?g=` → a grain. Absent and unknown ⇒ {@link DEFAULT_GRAIN} = `week` (HOF-96 §1).
 *
 * The zero value used to be `day`, on the rationale that it was what the chart
 * drew before this control existed. That rationale expired with the control:
 * HOF-6's own opening argument is that one point per day over a fixed year IS
 * the noise the grain switcher was added to escape, and a default is what a
 * cold open — a farmer following a link, or opening the app for the first time —
 * actually gets. Preserving the pre-control behaviour as the default preserved
 * the problem, for exactly the readers least able to name it.
 *
 * `week` rather than `month`: it is the finest grain that still folds a season
 * of daily deliveries into a readable line, and it is the grain the neighbouring
 * ernteprognose chart's Zeitraster opens on, so a reader switching tabs sees the
 * same resolution.
 *
 * `day` is still ACCEPTED — it is now a non-default value, not a retired one, so
 * a `?g=day` link minted before this change keeps meaning what it did.
 *
 * `year` — a valid ernteprognose grain — is deliberately NOT accepted: the axis
 * here is exactly one year wide, so a year bucket is a single point, and
 * silently rendering one would look like a broken chart rather than a coarse
 * one. It degrades to the default like any other junk.
 *
 * ASCII, lowercase, English tokens: the repo's URL habit (`?g=week`,
 * `?view=kanal`), and shared verbatim with ernteprognose so one reader's muscle
 * memory transfers.
 */
export function resolveGrain(param: string | null): Grain {
  return param === 'day' || param === 'month' ? param : DEFAULT_GRAIN;
}

/** UTC epoch-day integer → ISO `YYYY-MM-DD`. */
export function epochDayToIso(day: number): string {
  return new Date(day * 86_400_000).toISOString().slice(0, 10);
}

/** The epoch day on which `e`'s bucket STARTS. */
export function bucketStartEpochDay(e: number, grain: Grain): number {
  switch (grain) {
    case 'day':
      return e;
    case 'week':
      // Epoch day 0 (1970-01-01) is a THURSDAY, so +3 rotates the week to start
      // on Monday. Pure integer arithmetic — no Date, no timezone, and correct
      // for negative epoch days too (the double modulo).
      return e - ((((e + 3) % 7) + 7) % 7);
    case 'month':
      return toEpochDay(`${epochDayToIso(e).slice(0, 7)}-01`);
  }
}

/**
 * The epoch day the NEXT bucket starts on, given this bucket's start.
 *
 * Calendar months, never 30-day steps: a month bucket must line up with the
 * month it is named after, or the label and the average disagree about which
 * deals were counted.
 */
export function nextBucketEpochDay(startE: number, grain: Grain): number {
  switch (grain) {
    case 'day':
      return startE + 1;
    case 'week':
      return startE + 7;
    case 'month': {
      const [y, m] = epochDayToIso(startE).split('-').map(Number);
      const nextY = m === 12 ? y + 1 : y;
      const nextM = m === 12 ? 1 : m + 1;
      return toEpochDay(`${nextY}-${String(nextM).padStart(2, '0')}-01`);
    }
  }
}

/** The LAST epoch day this bucket covers (inclusive). */
export function bucketEndEpochDay(startE: number, grain: Grain): number {
  return nextBucketEpochDay(startE, grain) - 1;
}

const DE_MONTHS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

/** German `DD.MM.YYYY`. */
export function formatDayDe(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

/**
 * What one point STANDS FOR, in the reader's language — a period, not a date.
 *
 * A weekly average captioned "17.08.2026" reads as one Monday's trading, which
 * is exactly the misreading the old candle mode had to fix with its own
 * `date–endDate` label. The day panel and the selected-point readout both take
 * their heading from here, so the two cannot describe the same bucket
 * differently.
 *
 * The week form drops the repeated year (`17.08.–23.08.2026`) and restores it
 * across a rollover (`28.12.2026–03.01.2027`), where dropping it would be
 * ambiguous rather than merely redundant.
 */
export function bucketLabel(date: string, endDate: string, grain: Grain): string {
  if (grain === 'day') return formatDayDe(date);
  if (grain === 'month') {
    const [y, m] = date.split('-').map(Number);
    return `${DE_MONTHS[m - 1]} ${y}`;
  }
  const [fy, fm, fd] = date.split('-');
  const [ty, tm, td] = endDate.split('-');
  return fy === ty ? `${fd}.${fm}.–${td}.${tm}.${ty}` : `${fd}.${fm}.${fy}–${td}.${tm}.${ty}`;
}
